import React from 'react';
import { useSEO } from '@/hooks/useSEO';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  MessageSquare,
  BookOpen,
  Lightbulb,
  Zap,
  Sparkles,
  Target,
  Brain,
  ArrowRight,
  GraduationCap,
  LayoutList,
  FileText,
  Wrench,
  Flag,
  BarChart3,
} from 'lucide-react';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';

const featureCategories = [
  {
    title: 'AI Coach',
    description: 'Your personalized career strategist powered by AI',
    icon: MessageSquare,
    iconBg: 'bg-primary/15 text-primary',
    gradient: 'from-primary/10 to-violet-500/10',
    features: [
      {
        title: 'AI Career Coach',
        description: 'Chat with an AI coach that understands your goals and delivers strategic advice. Get tailored recommendations for stakeholder buy-in, leadership, and career growth.',
        href: '/coach',
        cta: 'Open Coach',
        icon: MessageSquare,
      },
    ],
  },
  {
    title: 'Learning',
    description: 'Personalized paths and daily lessons',
    icon: GraduationCap,
    iconBg: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
    gradient: 'from-emerald-500/10 to-teal-500/10',
    features: [
      {
        title: 'Learning Plan',
        description: 'Your personalized learning journey with daily lessons, objectives, and progress tracking. Complete lessons and manage your growth path.',
        href: '/learning-plan',
        cta: 'View Plan',
        icon: LayoutList,
      },
      {
        title: 'Lessons',
        description: 'Bite-sized lessons tailored to your goals. View, start, and complete lessons with full content and markdown support.',
        href: '/learning-plan',
        cta: 'Explore Lessons',
        icon: BookOpen,
      },
    ],
  },
  {
    title: 'Nudges & Tips',
    description: 'Daily motivation and actionable advice',
    icon: Lightbulb,
    iconBg: 'bg-amber-500/15 text-amber-600 dark:text-amber-400',
    gradient: 'from-amber-500/10 to-orange-500/10',
    features: [
      {
        title: 'Nudges',
        description: 'Daily nudges delivered in the Coach to keep you on track. Motivation, habit-building, and progress check-ins.',
        href: '/coach',
        cta: 'See Nudges',
        icon: Zap,
      },
      {
        title: 'Tips',
        description: 'Quick tips for productivity, learning strategies, and goal achievement. Curated advice from experts.',
        href: '/coach',
        cta: 'Get Tips',
        icon: Lightbulb,
      },
    ],
  },
  {
    title: 'Neo & Knowledge',
    description: 'Smart content and knowledge base',
    icon: Brain,
    iconBg: 'bg-violet-500/15 text-violet-600 dark:text-violet-400',
    gradient: 'from-violet-500/10 to-purple-500/10',
    features: [
      {
        title: 'Neo Content',
        description: 'AI-powered content and knowledge integrated across Coach and Learning. Context-aware recommendations.',
        href: '/coach',
        cta: 'Discover Neo',
        icon: Sparkles,
      },
      {
        title: 'Skill Knowledge',
        description: 'Deep dives into skills, occupations, and career paths. Pain points, wins, and learning resources.',
        href: '/coach',
        cta: 'Explore',
        icon: FileText,
      },
    ],
  },
  {
    title: 'Tidbits & Hacks',
    description: 'Quick insights and productivity shortcuts',
    icon: Zap,
    iconBg: 'bg-blue-500/15 text-blue-600 dark:text-blue-400',
    gradient: 'from-blue-500/10 to-cyan-500/10',
    features: [
      {
        title: 'Tidbits',
        description: 'Bite-sized insights, facts, and trends. Consume quick knowledge on the go.',
        href: '/coach',
        cta: 'Browse Tidbits',
        icon: FileText,
      },
      {
        title: 'Hacks',
        description: 'Productivity hacks and efficiency shortcuts. Learn from proven techniques and workflows.',
        href: '/coach',
        cta: 'Get Hacks',
        icon: Wrench,
      },
    ],
  },
  {
    title: 'Goals & Journey',
    description: 'Track progress and stay accountable',
    icon: Target,
    iconBg: 'bg-rose-500/15 text-rose-600 dark:text-rose-400',
    gradient: 'from-rose-500/10 to-pink-500/10',
    features: [
      {
        title: 'Goals',
        description: 'Short-term and long-term goals aligned with your learning plan. Progress tracking and milestones.',
        href: '/coach',
        cta: 'View Goals',
        icon: Flag,
      },
      {
        title: 'Progress',
        description: 'Visual progress charts, day-by-day completion, and journey overview. Celebrate wins.',
        href: '/learning-plan',
        cta: 'See Progress',
        icon: BarChart3,
      },
    ],
  },
];

export const FeaturesPage: React.FC = () => {
  useSEO({ title: 'Features - AI Career Coach, Learning Paths & Daily Nudges', description: 'Explore Five AI features: AI coaching, personalized learning paths, micro-lessons, daily nudges, progress analytics, and executive presence training. Built for busy professionals.', path: '/features' });
  const navigate = useNavigate();

  return (
    <main id="main-content" className="min-h-screen bg-background" role="main" aria-label="Features">
      <Header />
      <div className="pt-16">
        {/* Hero */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,hsl(var(--primary)/0.18),transparent_50%)] dark:bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,hsl(var(--primary)/0.25),transparent_50%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_80%_20%,hsl(263_70%_50%/0.08),transparent)]" />
          <div className="container relative mx-auto px-4 py-24 sm:py-32">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center max-w-4xl mx-auto"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-8"
              >
                <Sparkles className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium text-primary">Everything we offer</span>
              </motion.div>
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-[1.1] tracking-tight">
                <span className="text-foreground">Features that</span>
                <br />
                <span className="bg-gradient-to-r from-primary via-violet-500 to-fuchsia-500 bg-clip-text text-transparent">
                  power your growth
                </span>
              </h1>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-4">
                AI Coach, personalized learning paths, daily nudges, tips, tidbits, hacks, and more—all designed to help you achieve your career goals.
              </p>
              <p className="text-sm text-muted-foreground/80">
                6 product areas · Built for measurable outcomes
              </p>
            </motion.div>
          </div>
        </section>

        {/* Feature categories */}
        <section className="py-4 sm:py-8">
          <div className="container mx-auto px-4 max-w-6xl">
            <div className="space-y-24">
              {featureCategories.map((category) => (
                <motion.div
                  key={category.title}
                  initial={{ opacity: 0, y: 32 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.5, delay: 0 }}
                  className="relative"
                >
                  {/* Category header */}
                  <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
                    <div className="flex items-start gap-4">
                      <div className={`w-14 h-14 rounded-2xl ${category.iconBg} flex items-center justify-center shrink-0 shadow-sm`}>
                        <category.icon className="w-7 h-7" />
                      </div>
                      <div>
                        <h2 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight">
                          {category.title}
                        </h2>
                        <p className="text-muted-foreground mt-1 max-w-xl">
                          {category.description}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Feature cards */}
                  <div className="grid md:grid-cols-2 gap-6">
                    {category.features.map((feature, featIndex) => (
                      <motion.div
                        key={feature.title}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4, delay: featIndex * 0.08 }}
                      >
                        <Card
                          hover
                          className={`h-full p-6 sm:p-8 rounded-2xl border-2 bg-gradient-to-br ${category.gradient} hover:border-primary/40 transition-all duration-300 group overflow-hidden`}
                        >
                          <div className="flex flex-col h-full">
                            <div className={`w-11 h-11 rounded-xl ${category.iconBg} flex items-center justify-center mb-5 shrink-0`}>
                              <feature.icon className="w-5 h-5" />
                            </div>
                            <h3 className="text-lg sm:text-xl font-semibold text-foreground mb-3 group-hover:text-primary transition-colors">
                              {feature.title}
                            </h3>
                            <p className="text-muted-foreground flex-1 mb-6 leading-relaxed">
                              {feature.description}
                            </p>
                            <Link
                              to={feature.href}
                              className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
                            >
                              {feature.cta}
                              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </Link>
                          </div>
                        </Card>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative py-20 sm:py-24 border-t border-border">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/[0.03] to-transparent dark:via-primary/[0.06]" />
          <div className="container relative mx-auto px-4 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-2xl mx-auto"
            >
              <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4">
                Ready to get started?
              </h2>
              <p className="text-muted-foreground mb-8 text-lg">
                Jump into the Coach or create your learning plan to begin your career transformation.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Button
                  size="lg"
                  onClick={() => navigate('/coach')}
                  className="rounded-xl gap-2 shadow-lg shadow-primary/20"
                >
                  <MessageSquare className="w-5 h-5" />
                  Open Coach
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => navigate('/learning-plan')}
                  className="rounded-xl gap-2 border-2"
                >
                  <BookOpen className="w-5 h-5" />
                  Learning Plan
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => navigate('/onboarding')}
                  className="rounded-xl gap-2 border-2"
                >
                  Get Started
                </Button>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
      <Footer />
    </main>
  );
};
